import { env } from '@/lib/env';
/**
 * Session Management Hook — HARD-011
 *
 * Session ID is now minted server-side and delivered as an HttpOnly cookie
 * (stork_sid) by POST /api/customer/session/init.  The cookie is sent
 * automatically on every request when `withCredentials: true` is set on the
 * axios instance, so callers no longer need to read or forward a session ID.
 *
 * Dual-mode transition (2 weeks post-deploy):
 *   - New sessions: server sets stork_sid cookie; no JS-readable ID needed.
 *   - Old in-flight sessions: backend falls back to X-Session-ID header;
 *     those sessions keep working until the client refreshes and gets the cookie.
 *
 * The localStorage-based approach has been removed.  `getSessionId()` and
 * `setSessionId()` are kept as no-ops for a single release cycle so call
 * sites that have not yet been updated do not break at compile time.
 * They will be deleted in the next cleanup pass.
 */

import { useEffect, useState } from 'react';

const apiBase = env.API_URL;

/**
 * Hook that initialises the server-minted session cookie on mount.
 * Renders children after the init call completes (or best-effort on failure).
 */
export const useSession = () => {
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                await fetch(`${apiBase}/customer/session/init`, {
                    method: 'POST',
                    credentials: 'include',
                });
            } catch {
                // Network failure — best-effort.  Cart calls may still work via
                // the legacy X-Session-ID header path or an existing cookie.
            } finally {
                if (!cancelled) setInitialized(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { initialized };
};

// ---------------------------------------------------------------------------
// Legacy shims — kept for a 2-week transition; delete after next cleanup pass
// ---------------------------------------------------------------------------

/**
 * @deprecated HARD-011: Session ID is now managed server-side via cookie.
 * This function is a no-op shim.  Remove callers in the next cleanup pass.
 */
export const getSessionId = (): string => '';

/**
 * @deprecated HARD-011: Session ID is now managed server-side via cookie.
 * This function is a no-op shim.  Remove callers in the next cleanup pass.
 */
export const setSessionId = (_sessionId: string): void => {
    // no-op
};

/**
 * @deprecated HARD-011: Session ID is now managed server-side via cookie.
 * This function is a no-op shim.  Remove callers in the next cleanup pass.
 */
export const clearSessionId = (): void => {
    // no-op — nothing to clear; cookie is HttpOnly
};

export default useSession;
